import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import {
  UnitTransferJointDematCheckStatusEnumType,
  UnitTransferReasonEnumType,
  UnitTransferRequestStatusEnumType,
} from '__generated__/graphql';
import { FC } from 'react';
import { useParams } from 'react-router-dom';

import Badge from 'primitives/Badge';
import LoadingIndicator from 'primitives/LoadingIndicator';

import DetailsPanel from 'components/DetailsPanel';
import ErrorMessage from 'components/ErrorMessage';
import SectionCard from 'components/SectionCard';

import unitTransferReasonToReadable from 'utils/enums/unit-transfer-reason-to-readable';
import unitTransferRequestStatusToReadable from 'utils/enums/unit-transfer-request-status-to-readable';
import unitTransferUnitsLocationToReadable from 'utils/enums/unit-transfer-units-location-to-readable';

import AtAGlancePanel from './AtAGlancePanel';
import EscrowExceptionPanel from './EscrowExceptionPanel';
import SchemesTable from './SchemesTable';
import UnitTransferActions from './UnitTransferActions';
import UnitTransferTimeline from './UnitTransferTimeline';
import deriveUnitTransferBlockers from './derive-blockers';

const UNIT_TRANSFER_REQUEST_OVERVIEW_QUERY = gql(`
  query getUnitTransferRequestOverview($id: ID!) {
    unitTransferRequest(id: $id) {
      id
      status
      reason
      reasonDescription
      relationship
      unitsLocation
      createdAt
      rejectionReason
      slaDaysRemaining
      valuationReportRequested
      jointDematCheckStatus
      transferor { name }
      transferee { name }
      transferorEntity { name }
      documentChecklistProgress {
        approvedCount
        requiredCount
      }
      documents {
        id
        status
        fulfilmentMode
        isRequired
        checks {
          id
          severity
          humanVerdict
        }
      }
      signatureRequests {
        id
        status
      }
      escrowException {
        id
        status
        note
        opsApprovedAt
        legalApprovedAt
      }
      schemes {
        id
        schemeName
        isin
        numberOfUnits
        investmentAmount
      }
      events {
        id
        type
        note
        createdAt
        actor { name }
      }
    }
  }
`);

const StatusBadge: FC<{ value: UnitTransferRequestStatusEnumType }> = ({ value }) => {
  const { label, color } = unitTransferRequestStatusToReadable(value);
  return <Badge label={label} color={color} showIndicator />;
};

const ReasonBadge: FC<{ value: UnitTransferReasonEnumType }> = ({ value }) => {
  const { label, color } = unitTransferReasonToReadable(value);
  return <Badge label={label} color={color} />;
};

const UnitTransferInformation: FC = () => {
  const { unitTransferId } = useParams<{ unitTransferId: string }>() as { unitTransferId: string };

  const { loading, error, data, refetch } = useQuery(UNIT_TRANSFER_REQUEST_OVERVIEW_QUERY, {
    variables: { id: unitTransferId },
  });

  if (loading && !data) return <LoadingIndicator />;
  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const unitTransferRequest = data.unitTransferRequest;

  const blockers = deriveUnitTransferBlockers({
    documents: unitTransferRequest.documents,
    signatureRequests: unitTransferRequest.signatureRequests,
  });

  return (
    <div className="flex flex-col gap-y-4">
      <UnitTransferActions
        requestId={unitTransferId}
        status={unitTransferRequest.status}
        valuationReportRequested={unitTransferRequest.valuationReportRequested}
      />

      <AtAGlancePanel
        documentsApproved={unitTransferRequest.documentChecklistProgress.approvedCount}
        documentsRequired={unitTransferRequest.documentChecklistProgress.requiredCount}
        blockers={blockers}
        slaDaysRemaining={unitTransferRequest.slaDaysRemaining}
        jointDematCheckStatus={
          unitTransferRequest.jointDematCheckStatus as UnitTransferJointDematCheckStatusEnumType
        }
      />

      {/*
        Every per-stage timestamp this panel used to carry (conditionsIssuedAt, documentsVerifiedAt,
        effectiveDate, executedAt, readyForDisAt, rejectedAt) is in the Timeline below, with the
        actor who caused it. Duplicating them here pushed the identity fields ops actually scans
        for below the fold.
      */}
      <DetailsPanel
        title="Transfer Request"
        data={[
          { label: 'Transferor', value: unitTransferRequest.transferor.name },
          { label: 'Transferee', value: unitTransferRequest.transferee.name },
          { label: 'Transferor Entity', value: unitTransferRequest.transferorEntity.name },
          {
            label: 'Status',
            value: unitTransferRequest.status,
            type: 'CUSTOM',
            customValue: StatusBadge,
          },
          {
            label: 'Reason',
            value: unitTransferRequest.reason,
            type: 'CUSTOM',
            customValue: ReasonBadge,
          },
          { label: 'Reason Description', value: unitTransferRequest.reasonDescription },
          { label: 'Relationship', value: unitTransferRequest.relationship },
          {
            label: 'Units Location',
            value: unitTransferUnitsLocationToReadable(unitTransferRequest.unitsLocation),
          },
          { label: 'Created At', value: unitTransferRequest.createdAt, type: 'DATETIME' },
          { label: 'Rejection Reason', value: unitTransferRequest.rejectionReason },
        ]}
      />

      {unitTransferRequest.escrowException && (
        <EscrowExceptionPanel
          requestId={unitTransferId}
          escrowException={unitTransferRequest.escrowException}
        />
      )}

      <SchemesTable schemes={unitTransferRequest.schemes} />

      <SectionCard title="Timeline">
        <UnitTransferTimeline events={unitTransferRequest.events} />
      </SectionCard>
    </div>
  );
};

export default UnitTransferInformation;
